/**
 * Ranking payers for the claim-editor lookup.
 *
 * ## Why ranking, and not just a bigger list
 *
 * The payer list arrives sorted `NAME ASCENDING` and the filter is a stable
 * `Array.filter`, so whatever survives is still alphabetical. Truncating that
 * takes the alphabetically-first N matches — which is not the same as the best
 * N matches, and on a large client it is not even close.
 *
 * Typing `medicare` against a client with 100+ matching payers returned
 * *AARP Medicare Supplement*, *ACE Medicare Supplement*, *Aetna Medicare*, …
 * — the A's and B's — while a payer literally called **Medicare Part B** sorts
 * under M and fell past the cutoff. The closest possible match to what was typed
 * was the one thing the operator could not see.
 *
 * Raising the cap does not fix that; it moves the cliff. So matches are scored
 * and ordered BEFORE truncation, and the list stays alphabetical within each
 * tier so the ordering is still predictable.
 *
 * ## Why this is a module rather than a closure
 *
 * It used to live inline in a `loadOptions` prop, where it could not be unit
 * tested. `jest.services.config.js` covers `src/services`, so the logic is
 * testable here and the component keeps only the rendering concern.
 */

/** Everything the ranker needs. Deliberately structural — support and provider payers both fit. */
export interface ISearchablePayer {
    name?: string;
    coverage?: string;
    nickname?: string;
}

/**
 * Match quality, best first. Lower sorts earlier.
 *
 * Ordered by how directly the payer answers what was typed: the name being the
 * query beats the name starting with it, which beats the query appearing as a
 * whole word, and anything matched only through coverage or nickname comes last
 * because the operator was almost certainly typing a name.
 */
export enum PayerMatchRank {
    ExactName = 0,
    NameStartsWith = 1,
    NameWordBoundary = 2,
    NameContains = 3,
    AllTermsInName = 4,
    OtherFieldOnly = 5,
}

const normalize = (value?: string): string => (value ? value.toLowerCase().trim() : '');

/** Escape a user-typed string so it can sit inside a RegExp. Queries contain `(`, `+`, `.` routinely. */
const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** True when `query` appears in `text` starting at a word boundary. */
const matchesAtWordBoundary = (text: string, query: string): boolean => {
    if (!text || !query) return false;
    return new RegExp(`\\b${escapeForRegExp(query)}`).test(text);
};

/**
 * Score one payer against an already-normalized query and its split terms.
 *
 * Assumes the payer has already passed the filter — this only orders survivors.
 */
export function rankPayerMatch(payer: ISearchablePayer, query: string, terms: string[]): PayerMatchRank {
    const name = normalize(payer?.name);

    if (name && query) {
        if (name === query) return PayerMatchRank.ExactName;
        if (name.startsWith(query)) return PayerMatchRank.NameStartsWith;
        if (matchesAtWordBoundary(name, query)) return PayerMatchRank.NameWordBoundary;
        if (name.includes(query)) return PayerMatchRank.NameContains;
    }

    // Multi-word queries rarely appear verbatim: "blue shield hmo" against
    // "Blue Shield CA HMO". Every word being in the NAME still beats a payer
    // that only qualified because a word landed in its coverage code.
    if (name && terms.length > 1 && terms.every(term => name.includes(term))) {
        return PayerMatchRank.AllTermsInName;
    }

    return PayerMatchRank.OtherFieldOnly;
}

export interface IPayerSearchResult<T> {
    /** The matches to show, ranked then truncated. */
    matches: T[];
    /** How many matches were dropped by `limit`. Zero when nothing was hidden. */
    hidden: number;
    /** Every match, before truncation — useful for counts and tests. */
    total: number;
}

/**
 * Filter payers by query, order by match quality, then truncate.
 *
 * Matching is AND across the query's words, over name + coverage + nickname
 * combined — a single `includes` of the whole query is an exact-substring match
 * and finds nothing for "blue shield hmo" against "Blue Shield CA HMO", because
 * the "CA" sits between the words.
 *
 * AND rather than OR is deliberate: with OR each extra word WIDENS the results,
 * so a longer and more specific query returns more rows instead of fewer, which
 * is the opposite of what typing more is for.
 *
 * An empty query returns the head of the list unranked — there is nothing to
 * rank against, and the incoming alphabetical order is the sensible default.
 */
export function searchPayers<T extends ISearchablePayer>(
    payers: T[],
    rawQuery: string,
    limit: number,
): IPayerSearchResult<T> {
    const list = payers || [];
    const query = normalize(rawQuery);
    const terms = query.split(/\s+/).filter(Boolean);

    if (!terms.length) {
        return { matches: list.slice(0, limit), hidden: Math.max(0, list.length - limit), total: list.length };
    }

    const filtered = list.filter(payer => {
        const haystack = [normalize(payer?.name), normalize(payer?.coverage), normalize(payer?.nickname)].join(' ');
        return terms.every(term => haystack.includes(term));
    });

    // Stable sort — ES2019 guarantees it — so payers sharing a rank keep the
    // incoming alphabetical order rather than being shuffled arbitrarily.
    const ranked = filtered
        .map((payer, index) => ({ payer, index, rank: rankPayerMatch(payer, query, terms) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(entry => entry.payer);

    return {
        matches: ranked.slice(0, limit),
        hidden: Math.max(0, ranked.length - limit),
        total: ranked.length,
    };
}
